import React, { useState, useEffect } from 'react';
import { 
  Firestore,
  collection,
  query,
  where,
  getDocs,
  Timestamp
} from 'firebase/firestore';
import { Functions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../../hooks/AuthProvider';
import LoadingPage from '../../LoadingPage';
import AddStoreWorkflow from '../AddStore/AddStoreWorkflow';
import StoreDetails from '../StoreDetails/StoreDetails';
import StripeConnectionModal from './StripeConnectionModal';
import './StoreManagement.css';

// Basic App interface - matching ControlPanel
interface App {
  id: string;
  name: string;
  type?: string;
  region?: string;
  organizationId: string;
  appIcon?: string;
  status?: string;
}

// Store interface with Stripe support - matching our modernized structure
interface Store {
  storeId?: string;
  name: string;
  description?: string;
  address?: string;
  city: string;
  state?: string;
  zipCode?: string;
  country: string;
  phone?: string;
  email?: string;
  website?: string;
  timezone?: string;
  currency?: string;
  appId: string;
  orgId: string;
  stripeAccount: string;
  // Test environment Stripe details (display only)
  stripeTestAccountId?: string;
  stripeTestAccountName?: string;
  stripeTestConnected: boolean;
  stripeTestConnectUrl?: string;
  stripeTestConnectUrlExpires?: Date | any;
  stripeTestOnboardingStarted?: Date | any;
  stripeTestOnboardingCompletedAt?: Date | any;
  stripeTestConnectedAt?: Date | any;
  // Live environment Stripe details
  stripeLiveAccountId?: string;
  stripeLiveAccountName?: string;
  stripeLiveConnected: boolean;
  stripeLiveConnectUrl?: string;
  stripeLiveConnectUrlExpires?: Date | any;
  stripeLiveOnboardingStarted?: Date | any;
  stripeLiveOnboardingCompletedAt?: Date | any;
  stripeLiveConnectedAt?: Date | any;
  // Legacy fields for backward compatibility
  stripeAccountId?: string;
  stripeAccountName?: string;
  stripeConnected: boolean;
  stripeConnectUrl?: string;
  stripeConnectUrlExpires?: Date | any;
  stripeOnboardingStarted?: Date | any;
  isActive: boolean;
  createdAt: Date | any;
  updatedAt: Date | any;
}

interface StoreManagementProps {
  db: Firestore;
  functions: Functions;
  appData: App;
  selectedOrganizationId: string;
  onBack: () => void;
  onBackToOrganizations: () => void;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
  autoShowAddStore?: boolean;
  enableTestStripe?: boolean; // New flag to enable/disable test Stripe functionality
}

const StoreManagement: React.FC<StoreManagementProps> = ({
  db,
  functions,
  appData,
  selectedOrganizationId,
  onBack,
  onBackToOrganizations,
  onError,
  onSuccess,
  setLoading,
  autoShowAddStore = false,
  enableTestStripe = true 
}) => {
  const { currentUser } = useAuth();
  const [stores, setStores] = useState<Store[]>([]);
  const [localLoading, setLocalLoading] = useState(true);
  const [showAddStore, setShowAddStore] = useState(autoShowAddStore);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [showStripeConnection, setShowStripeConnection] = useState(false);

  // Cloud function references
  const createStoreFunc = httpsCallable(functions, 'createStore');
  const completeStoreSetupFunc = httpsCallable(functions, 'completeStoreSetup');

  // Handle Stripe return URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const accountId = urlParams.get('account_id');
    const storeId = urlParams.get('store_id');
    const appId = urlParams.get('app_id');
    const environment = urlParams.get('environment') || 'live';

    if (accountId && storeId && appId && appId === appData.id) {
      handleStripeReturn(accountId, storeId, appId, environment);
    }
  }, [appData.id]);

  const handleStripeReturn = async (accountId: string, storeId: string, appId: string, environment: string) => {
    setLoading(true);
    try {
      const result = await completeStoreSetupFunc({
        accountId,
        storeId,
        appId,
        environment
      });

      interface CompleteSetupResponse {
        success: boolean;
        message: string;
      }

      const responseData = result.data as CompleteSetupResponse;
      
      if (responseData && responseData.success) {
        onSuccess(responseData.message);
        await loadStores();
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        onError('Store setup could not be completed');
      }
    } catch (err: any) {
      console.error('Error completing store setup:', err);
      onError(err.message || 'Failed to complete store setup');
    } finally {
      setLoading(false);
    }
  };

  // Load stores for this app
  const loadStores = async () => {
    console.log('🔍 DEBUG - Loading stores for app:', appData.id);
    
    try {
      const q = query(
        collection(db, 'stores'),
        where('appId', '==', appData.id)
      );
      const querySnapshot = await getDocs(q);
      
      const storesList: Store[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data() as Store;
        
        let createdAt: Date;
        if (data.createdAt instanceof Timestamp) {
          createdAt = data.createdAt.toDate();
        } else if (data.createdAt && typeof data.createdAt === 'object' && 'toDate' in data.createdAt) {
          createdAt = (data.createdAt as any).toDate();
        } else {
          createdAt = new Date(data.createdAt || Date.now());
        }
        
        let updatedAt: Date;
        if (data.updatedAt instanceof Timestamp) {
          updatedAt = data.updatedAt.toDate();
        } else if (data.updatedAt && typeof data.updatedAt === 'object' && 'toDate' in data.updatedAt) {
          updatedAt = (data.updatedAt as any).toDate();
        } else {
          updatedAt = new Date(data.updatedAt || Date.now());
        }
        
        storesList.push({
          ...data,
          storeId: doc.id,
          createdAt: createdAt,
          updatedAt: updatedAt,
          stripeTestConnected: data.stripeTestConnected || false,
          stripeLiveConnected: data.stripeLiveConnected || false,
          stripeConnected: data.stripeConnected || false,
          isActive: data.isActive ?? false
        });
      });

      console.log('✅ DEBUG - Loaded stores:', storesList.length);
      setStores(storesList);
      
      if (appData.type === 'single-store' && storesList.length === 0 && !autoShowAddStore) {
        setShowAddStore(true);
      }
    } catch (err) {
      console.error('❌ Error loading stores:', err);
      onError('Failed to load stores.');
    } finally {
      setLocalLoading(false);
    }
  };

  useEffect(() => {
    const initializeStores = async () => {
      setLocalLoading(true);
      await loadStores();
    };

    initializeStores();
  }, [appData.id]);

  useEffect(() => {
    if (autoShowAddStore && stores.length === 0 && !localLoading) {
      setShowAddStore(true);
    }
  }, [autoShowAddStore, stores.length, localLoading]);

  const getMaxStores = () => {
    switch (appData.type) {
      case 'single-store':
        return 1;
      case 'single-store-multi-location':
        return 10;
      case 'multi-store':
        return 100;
      default:
        return 1;
    }
  };

  const canAddMoreStores = () => {
    if (appData.type === 'single-store') {
      return stores.length === 0;
    }
    return stores.length < getMaxStores();
  };

  const handleAddStore = () => {
    setShowAddStore(true);
  };

  const handleStoreSubmit = async (storeData: Omit<Store, 'appId' | 'orgId' | 'stripeAccount'>) => {
    if (!currentUser?.email) {
      onError('User email not available.');
      return;
    }

    console.log('🔍 DEBUG - Creating store with data:', {
      storeData,
      appId: appData.id,
      organizationId: appData.organizationId
    });

    setLoading(true);
    try {
      const result = await createStoreFunc({ 
        storeData: storeData,
        appId: appData.id
      });

      interface CreateStoreResponse {
        success: boolean;
        storeId: string;
        message: string;
        appId: string;
        orgId: string;
        stripeAccount: string;
      }

      const responseData = result.data as CreateStoreResponse;
      
      if (responseData && responseData.success) {
        await loadStores();
        setShowAddStore(false);
        onSuccess(responseData.message || `Store "${storeData.name}" has been created successfully!`);
        console.log('✅ DEBUG - Store created successfully:', responseData.storeId);
      } else {
        onError('Failed to create store. Please try again.');
      }
    } catch (err: any) {
      console.error('❌ Error creating store:', err);
      onError(err.message || 'Failed to create store.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAddStore = () => {
    setShowAddStore(false);
    
    if (appData.type === 'single-store' && stores.length === 0) {
      onBack();
    }
    else {
      const initializeStores = async () => {
        setLocalLoading(true);
        await loadStores();
      };

      initializeStores();
    }
  };

  const handleViewStoreDetails = (store: Store) => {
    console.log('🔍 DEBUG - Viewing store details:', store.storeId);
    setSelectedStore(store);
  };

  const handleBackFromStoreDetails = () => {
    setSelectedStore(null);
  };

  const handleConnectStripe = (store: Store, environment: 'test' | 'live') => {
    // Prevent test connections when flag is disabled
    if (environment === 'test' && !enableTestStripe) {
      onError('Test Stripe connections are currently disabled.');
      return;
    }
    
    setSelectedStore(store);
    setStripeEnvironment(environment);
    setShowStripeConnection(true);
  };

  const [stripeEnvironment, setStripeEnvironment] = useState<'test' | 'live'>('live');

  if (localLoading) {
    return <LoadingPage message="Loading store management..." />;
  }

  // Show store details if selected
  if (selectedStore && !showStripeConnection) {
    return (
      <StoreDetails
        store={selectedStore}
        functions={functions}
        onBack={handleBackFromStoreDetails}
        onError={onError}
        onSuccess={onSuccess}
        setLoading={setLoading}
        onStoreUpdated={loadStores}
      />
    );
  }

  // Show add store workflow
  if (showAddStore) {
    return (
      <AddStoreWorkflow
        functions={functions}
        onSubmit={handleStoreSubmit}
        onCancel={handleCancelAddStore}
        appRegion={appData.region || 'us-east1'}
        organizationId={selectedOrganizationId}
        appId={appData.id}
        appName={appData.name}
        appType={appData.type || 'single-store'}
      />
    );
  }

  return (
    <>
      <div className="store-management">
        {/* Header */}
        <div className="store-management-header">
          <button className="store-management-back-btn" onClick={onBack}>
            ← Back to Control Panel
          </button>
          <h1>Store Management: {appData.name}</h1>
        </div>

        {/* Content */}
        <div className="store-management-content">
          <div className="store-management-info">
            <div className="store-count-info">
              <h3>Your Stores</h3>
              <div className="count-display">
                <span className="store-count">{stores.length}</span>
                <span className="store-limit">/ {getMaxStores()}</span>
                <span className="store-type">({appData.type})</span>
              </div>
            </div>

            <div className="store-actions">
              {canAddMoreStores() ? (
                <button
                  type="button"
                  className="add-store-btn"
                  onClick={handleAddStore}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add Store
                </button>
              ) : (
                <button
                  type="button"
                  className="request-more-stores-btn"
                  onClick={() => onSuccess('Request more stores coming soon!')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 9V5a3 3 0 0 0-6 0v4"/>
                    <rect x="2" y="9" width="20" height="11" rx="2" ry="2"/>
                  </svg>
                  Request More Stores
                </button>
              )}
            </div>
          </div>

          {stores.length === 0 ? (
            <div className="empty-stores-state">
              <div className="empty-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9,22 9,12 15,12 15,22"/>
                </svg>
              </div>
              <h4>No Stores Added Yet</h4>
              <p>Your app needs at least one store to be complete. Add your first store to get started.</p>
              <button
                type="button"
                className="add-first-store-btn"
                onClick={handleAddStore}
              >
                Add Your First Store
              </button>
            </div>
          ) : (
            <div className="stores-table-container">
              <table className="stores-table">
                <thead>
                  <tr>
                    <th>Store Name</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th>Stripe Test</th>
                    <th>Stripe Live</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stores.map((store) => (
                    <tr 
                      key={store.storeId} 
                      className="store-table-row"
                      onClick={() => handleViewStoreDetails(store)}
                    >
                      <td className="store-name-cell">
                        <div className="store-name-content">
                          <span className="store-name">{store.name}</span>
                          <span className="store-id">ID: {store.storeId}</span>
                        </div>
                      </td>
                      <td className="store-location-cell">
                        <div className="store-location-content">
                          <span className="store-city">{store.city}, {store.state || 'N/A'}</span>
                          <span className="store-country">{store.country}</span>
                        </div>
                      </td>
                      <td className="store-status-cell">
                        <span className={`status-indicator ${store.isActive ? 'active' : 'inactive'}`}>
                          {store.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="store-stripe-test-cell" onClick={(e) => e.stopPropagation()}>
                        {enableTestStripe ? (
                            <>{!store.stripeTestConnected ? (
                              <button 
                                type="button" 
                                className="connect-stripe-btn test"
                                onClick={() => handleConnectStripe(store, 'test')}
                              >
                                Connect Test
                              </button>
                            ) : (
                              <div className="stripe-connected-info">
                                <span className="stripe-status-badge test-connected">✓ Connected</span>
                                <button 
                                  type="button" 
                                  className="manage-stripe-btn test"
                                  onClick={() => handleConnectStripe(store, 'test')}
                                >
                                  Manage
                                </button>
                              </div>
                            )}</>
                        ) : (
                          <span className={`stripe-status ${store.stripeTestConnected ? 'connected' : 'not-connected'}`}>
                            {store.stripeTestConnected ? '✓ Connected' : 'Not Connected'}
                          </span>
                        )}
                      </td>
                      <td className="store-stripe-live-cell" onClick={(e) => e.stopPropagation()}>
                          {!store.stripeLiveConnected ? (
                            <button 
                              type="button" 
                              className="connect-stripe-btn live"
                              onClick={() => handleConnectStripe(store, 'live')}
                            >
                              Connect Live
                            </button>
                          ) : (
                            <div className="stripe-connected-info">
                              <span className="stripe-status-badge live-connected">✓ Connected</span>
                              <button 
                                type="button" 
                                className="manage-stripe-btn live"
                                onClick={() => handleConnectStripe(store, 'live')}
                              >
                                Manage
                              </button>
                            </div>
                          )}
                      </td>
                      <td className="store-actions-cell" onClick={(e) => e.stopPropagation()}>
                        <button 
                          type="button"
                          className="view-details-btn"
                          onClick={() => handleViewStoreDetails(store)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {stores.length === 0 && (
            <div className="app-incomplete-warning">
              <div className="warning-content">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <div>
                  <h4>App Incomplete</h4>
                  <p>Your app is not ready to accept orders until you add at least one store with live Stripe connection.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stripe Connection Modal */}
      {showStripeConnection && selectedStore && (
        <StripeConnectionModal
          store={selectedStore}
          appId={appData.id}
          organizationId={selectedOrganizationId}
          environment={stripeEnvironment}
          db={db}
          functions={functions}
          onSuccess={(message: string) => {
            onSuccess(message);
            loadStores();
          }}
          onError={onError}
          onCancel={() => {
            setShowStripeConnection(false);
            setSelectedStore(null);
          }}
          setLoading={setLoading}
        />
      )}
    </>
  );
};

export default StoreManagement;